import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { format, subDays, startOfMonth, endOfMonth, eachDayOfInterval, differenceInDays } from "date-fns";
import {
  BarChart3,
  Download,
  FileDown,
  Printer,
  Calendar,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Clock,
  CheckCircle2,
  XCircle,
  PieChart,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  PieChart as RechartsPieChart,
  Pie,
  Legend,
} from "recharts";
import BillingSidebar from "@/components/layout/BillingSidebar";
import BillingHeader from "@/components/layout/BillingHeader";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export default function ClaimsReports() {
  const [dateRange, setDateRange] = useState("30days");
  const { toast } = useToast();

  // Fetch claims data
  const { data: claims, isLoading } = useQuery({
    queryKey: ["claims-reports", dateRange],
    queryFn: async () => {
      const { data: sessions, error } = await supabase
        .from("telehealth_sessions")
        .select("*")
        .order("scheduled_date", { ascending: true });

      if (error) throw error;

      const memberIds = [...new Set(sessions?.map(s => s.member_id) || [])];
      const { data: intakeForms } = await supabase
        .from("patients")
        .select("user_id, insurance_type")
        .in("user_id", memberIds);

      return sessions?.map(s => ({
        ...s,
        insurance_type: intakeForms?.find(i => i.user_id === s.member_id)?.insurance_type || "medicare",
        amount: 125,
      })) || [];
    },
  });

  // Calculate metrics
  const metrics = useMemo(() => {
    if (!claims) return null;

    const today = new Date();
    const daysBack = dateRange === "7days" ? 7 : dateRange === "30days" ? 30 : 90;
    const startDate = subDays(today, daysBack);

    const filteredClaims = claims.filter(c => new Date(c.scheduled_date) >= startDate);
    const previousClaims = claims.filter(c => {
      const date = new Date(c.scheduled_date);
      return date >= subDays(startDate, daysBack) && date < startDate;
    });

    const currentRevenue = filteredClaims
      .filter(c => c.billing_status === "approved")
      .reduce((sum, c) => sum + c.amount, 0);

    const previousRevenue = previousClaims
      .filter(c => c.billing_status === "approved")
      .reduce((sum, c) => sum + c.amount, 0);

    const revenueChange = previousRevenue ? ((currentRevenue - previousRevenue) / previousRevenue) * 100 : 0;

    const totalSubmitted = filteredClaims.filter(c => 
      ["submitted", "pending", "approved", "denied"].includes(c.billing_status || "")
    ).length;

    const approved = filteredClaims.filter(c => c.billing_status === "approved").length;
    const denied = filteredClaims.filter(c => c.billing_status === "denied").length;
    const pending = filteredClaims.filter(c => c.billing_status === "pending" || c.billing_status === "submitted").length;

    return {
      totalRevenue: currentRevenue,
      revenueChange,
      totalClaims: filteredClaims.length,
      approvedClaims: approved,
      deniedClaims: denied,
      pendingClaims: pending,
      approvalRate: totalSubmitted ? (approved / totalSubmitted) * 100 : 0,
      denialRate: totalSubmitted ? (denied / totalSubmitted) * 100 : 0,
      avgDaysToPayment: 14,
    };
  }, [claims, dateRange]);

  // Revenue trend data
  const revenueTrendData = useMemo(() => {
    if (!claims) return [];

    const today = new Date();
    const daysBack = dateRange === "7days" ? 7 : dateRange === "30days" ? 30 : 90;
    const startDate = subDays(today, daysBack);

    const days = eachDayOfInterval({ start: startDate, end: today });
    
    return days.map(day => {
      const dayStr = format(day, "yyyy-MM-dd");
      const dayClaims = claims.filter(c => c.scheduled_date === dayStr);
      
      return {
        date: format(day, "MMM d"),
        revenue: dayClaims.filter(c => c.billing_status === "approved").reduce((sum, c) => sum + c.amount, 0),
        submitted: dayClaims.length,
      };
    });
  }, [claims, dateRange]);

  // Status distribution data
  const statusData = useMemo(() => {
    if (!metrics) return [];

    return [
      { name: "Paid", value: metrics.approvedClaims, color: "hsl(var(--success))" },
      { name: "Pending", value: metrics.pendingClaims, color: "hsl(var(--warning))" },
      { name: "Denied", value: metrics.deniedClaims, color: "hsl(var(--destructive))" },
    ].filter(d => d.value > 0);
  }, [metrics]);

  // Payer distribution data
  const payerData = useMemo(() => {
    if (!claims) return [];

    const payerCounts = claims.reduce((acc, c) => {
      const payer = c.insurance_type || "medicare";
      acc[payer] = (acc[payer] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(payerCounts).map(([name, value]) => ({
      name: name === "dual_eligible" ? "Dual Eligible" : name.charAt(0).toUpperCase() + name.slice(1),
      value,
    }));
  }, [claims]);

  const exportReport = () => {
    if (!metrics || !claims) return;

    const reportData = {
      generatedAt: new Date().toISOString(),
      dateRange,
      metrics,
      claimsCount: claims.length,
    };

    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `claims-report-${format(new Date(), "yyyy-MM-dd")}.json`;
    a.click();
    URL.revokeObjectURL(url);

    toast({ title: "Report Exported", description: "Report has been downloaded successfully" });
  };

  return (
    <div className="flex min-h-screen bg-background">
      <BillingSidebar />
      <div className="flex-1 flex flex-col">
        <BillingHeader />
        <main className="flex-1 p-6 overflow-auto">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Claims Reports</h1>
                <p className="text-muted-foreground">Analytics and insights for claims performance</p>
              </div>
              <div className="flex gap-2">
                <Select value={dateRange} onValueChange={setDateRange}>
                  <SelectTrigger className="w-[160px]">
                    <Calendar className="h-4 w-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7days">Last 7 Days</SelectItem>
                    <SelectItem value="30days">Last 30 Days</SelectItem>
                    <SelectItem value="90days">Last 90 Days</SelectItem>
                  </SelectContent>
                </Select>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline">
                      <Download className="h-4 w-4 mr-2" />
                      Export
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem onClick={exportReport}>
                      <FileDown className="h-4 w-4 mr-2" />
                      Export Report
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => window.print()}>
                      <Printer className="h-4 w-4 mr-2" />
                      Print Report
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
              </div>
            ) : metrics && (
              <>
                {/* KPI Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground">Total Revenue</p>
                          <p className="text-2xl font-bold">${metrics.totalRevenue.toLocaleString()}</p>
                          <div className={`flex items-center text-sm mt-1 ${metrics.revenueChange >= 0 ? "text-success" : "text-destructive"}`}>
                            {metrics.revenueChange >= 0 ? <TrendingUp className="h-4 w-4 mr-1" /> : <TrendingDown className="h-4 w-4 mr-1" />}
                            <span>{Math.abs(metrics.revenueChange).toFixed(1)}% vs prev period</span>
                          </div>
                        </div>
                        <div className="h-12 w-12 bg-success/10 rounded-full flex items-center justify-center">
                          <DollarSign className="h-6 w-6 text-success" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground">Approval Rate</p>
                          <p className="text-2xl font-bold">{metrics.approvalRate.toFixed(1)}%</p>
                          <div className="flex items-center text-success text-sm mt-1">
                            <CheckCircle2 className="h-4 w-4 mr-1" />
                            <span>{metrics.approvedClaims} approved</span>
                          </div>
                        </div>
                        <div className="h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center">
                          <CheckCircle2 className="h-6 w-6 text-primary" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground">Denial Rate</p>
                          <p className="text-2xl font-bold">{metrics.denialRate.toFixed(1)}%</p>
                          <div className="flex items-center text-destructive text-sm mt-1">
                            <XCircle className="h-4 w-4 mr-1" />
                            <span>{metrics.deniedClaims} denied</span>
                          </div>
                        </div>
                        <div className="h-12 w-12 bg-destructive/10 rounded-full flex items-center justify-center">
                          <XCircle className="h-6 w-6 text-destructive" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground">Avg Days to Payment</p>
                          <p className="text-2xl font-bold">{metrics.avgDaysToPayment}</p>
                          <div className="flex items-center text-muted-foreground text-sm mt-1">
                            <Clock className="h-4 w-4 mr-1" />
                            <span>{metrics.pendingClaims} pending</span>
                          </div>
                        </div>
                        <div className="h-12 w-12 bg-warning/10 rounded-full flex items-center justify-center">
                          <Clock className="h-6 w-6 text-warning" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Charts */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Revenue Trend */}
                  <Card className="lg:col-span-2">
                    <CardHeader>
                      <CardTitle>Revenue Trend</CardTitle>
                      <CardDescription>Daily revenue from approved claims</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={revenueTrendData}>
                            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                            <XAxis dataKey="date" className="text-xs" />
                            <YAxis className="text-xs" />
                            <Tooltip 
                              contentStyle={{ 
                                backgroundColor: "hsl(var(--card))", 
                                border: "1px solid hsl(var(--border))",
                                borderRadius: "8px",
                              }} 
                            />
                            <Area
                              type="monotone"
                              dataKey="revenue"
                              stroke="hsl(var(--success))"
                              fill="hsl(var(--success) / 0.2)"
                              strokeWidth={2}
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Status Distribution */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Claims by Status</CardTitle>
                      <CardDescription>Distribution of claim outcomes</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <RechartsPieChart>
                            <Pie
                              data={statusData}
                              cx="50%"
                              cy="50%"
                              innerRadius={60}
                              outerRadius={100}
                              paddingAngle={5}
                              dataKey="value"
                            >
                              {statusData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                            <Tooltip />
                            <Legend />
                          </RechartsPieChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Payer Breakdown */}
                <Card>
                  <CardHeader>
                    <CardTitle>Claims by Payer</CardTitle>
                    <CardDescription>Volume distribution across insurance payers</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[200px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={payerData} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis type="number" className="text-xs" />
                          <YAxis dataKey="name" type="category" className="text-xs" width={100} />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: "hsl(var(--card))", 
                              border: "1px solid hsl(var(--border))",
                              borderRadius: "8px",
                            }} 
                          />
                          <Bar dataKey="value" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
